export const INVESTOR_PRODUCTS = [
  'Conforming',
  'FHA',
  'VA',
  'Non-QM',
  'Jumbo',
  'USDA',
  'DSCR',
  'Reverse',
  '2nd Mortgage',
  'HELOC',
  'ITIN',
  'Foreign National',
  'Construction',
  'Commercial',
  '5-8 Units'
] as const;

export type InvestorProduct = typeof INVESTOR_PRODUCTS[number];

const aliases: Record<string, InvestorProduct | InvestorProduct[]> = {
  conventional: 'Conforming',
  conforming: 'Conforming',
  qm: 'Conforming',
  fha: 'FHA',
  va: 'VA',
  jumbo: 'Jumbo',
  'non-qm': 'Non-QM',
  'non qm': 'Non-QM',
  nonqm: 'Non-QM',
  usda: 'USDA',
  dscr: 'DSCR',
  reverse: 'Reverse',
  '2nd/heloc': ['2nd Mortgage', 'HELOC'],
  '2nd / heloc': ['2nd Mortgage', 'HELOC'],
  '2nd mortgage': '2nd Mortgage',
  'second mortgage': '2nd Mortgage',
  heloc: 'HELOC',
  itin: 'ITIN',
  'foreign national': 'Foreign National',
  construction: 'Construction',
  commercial: 'Commercial',
  '5-8 unit': '5-8 Units',
  '5-8 units': '5-8 Units'
};

const canonical = new Set<string>(INVESTOR_PRODUCTS);

function normalizeKey(value: string): string {
  return value.trim().replace(/\s+/g, ' ').toLowerCase();
}

function fuzzyProductMatch(value: string): InvestorProduct[] {
  const key = normalizeKey(value);
  const has = (pattern: RegExp) => pattern.test(key);

  const patterns: Array<[InvestorProduct, RegExp]> = [
    ['HELOC', /\bheloc\b/],
    ['2nd Mortgage', /\b(?:2nd|second)[\s-]+(?:mortgage|lien|loan)\b/],
    ['FHA', /\bfha\b/],
    ['VA', /\bva\b/],
    ['USDA', /\busda\b/],
    ['DSCR', /\bdscr\b/],
    ['Jumbo', /\bjumbo\b/],
    ['Reverse', /\breverse\b/],
    ['ITIN', /\bitin\b/],
    ['Foreign National', /\bforeign\s+national\b/],
    ['Construction', /\bconstruction\b/],
    ['Commercial', /\bcommercial\b/],
    ['5-8 Units', /\b5\s*(?:to|-)\s*8\s+units?\b/]
  ];
  const specific = patterns.filter(([, pattern]) => has(pattern)).map(([product]) => product);
  // Keep conflicting specific labels visible to the resolver. Non-QM is an
  // umbrella: a "Non-QM DSCR" program has the more specific DSCR scope.
  if (specific.length > 0) return specific;
  if (has(/\bnon[\s-]?qm\b/) || has(/\bnonqualified\b/)) return ['Non-QM'];
  if (has(/\b(conforming|conventional|fnma|fannie|fhlmc|freddie|agency|qm)\b/)) return ['Conforming'];

  return [];
}

export function normalizeInvestorProduct(value: unknown): InvestorProduct[] {
  if (typeof value !== 'string') {
    return [];
  }
  const raw = value.trim();
  if (!raw) {
    return [];
  }
  if (canonical.has(raw)) {
    return [raw as InvestorProduct];
  }
  const alias = aliases[normalizeKey(raw)];
  if (alias) {
    return Array.isArray(alias) ? alias : [alias];
  }
  return fuzzyProductMatch(raw);
}

export function resolveLoanProduct(input: {
  rateLockLoanProgram?: unknown;
  transDetailsLoanType?: unknown;
  cxDocType?: unknown;
}): { product: InvestorProduct | null; source: string; confidence: 'high' | 'medium' | 'none' } {
  // Encompass program labels may start with an investor and channel. Product
  // words in that company name do not describe the loan's program.
  const program = typeof input.rateLockLoanProgram === 'string'
    ? input.rateLockLoanProgram.replace(/^.*?\s+-\s+(?:correspondent|non-delegated|delegated)\s+-\s+/i, '')
    : input.rateLockLoanProgram;
  const candidates: Array<[string, unknown]> = [
    ['trans_details_loan_type', input.transDetailsLoanType],
    ['cx_doc_type', input.cxDocType],
    ['rate_lock_loan_program', program]
  ];

  const evidence = candidates.map(([source, value]) => {
    const products = normalizeInvestorProduct(value);
    // Documentation identifies bank-statement loans even when a branded lock
    // program omits Non-QM and Encompass reports the broad Conventional type.
    const bankStatement = source === 'cx_doc_type' && typeof value === 'string'
      && /\bbank[\s_-]+statements?\b/i.test(value);
    return {
      source,
      products: bankStatement && products.every(product => product === 'Conforming' || product === 'Non-QM')
        ? ['Non-QM' as const]
        : products
    };
  });
  const direct = evidence[0];
  if (direct.products.length === 1 && ['FHA', 'VA', 'USDA'].includes(direct.products[0])) {
    return { product: direct.products[0], source: direct.source, confidence: 'high' };
  }

  const specific = [...new Set(evidence.flatMap(({ products }) => products)
    .filter((product) => product !== 'Conforming' && product !== 'Non-QM'))];
  if (specific.length > 1) {
    return { product: null, source: 'unresolved', confidence: 'none' };
  }

  // Conventional describes a broad loan type and must not mask an explicit
  // Non-QM, DSCR, Jumbo, or second-lien product in the other Encompass fields.
  const product = specific[0]
    ?? (evidence.some((item) => item.products.includes('Non-QM')) ? 'Non-QM' : null)
    ?? (evidence.some((item) => item.products.includes('Conforming')) ? 'Conforming' : null);
  if (product) {
    const source = evidence.find((item) => item.products.includes(product))!.source;
    return { product, source, confidence: 'high' };
  }

  return { product: null, source: 'unresolved', confidence: 'none' };
}

export function selectTemplateForProduct<T extends { id: string }>(
  templates: T[],
  assignments: Array<{ template_id: string; product: string }>,
  product: InvestorProduct | null
): { template: T | null; assignmentProduct?: InvestorProduct; reason: 'ok' | 'no_template' | 'unresolved_product' | 'no_assignment' | 'ambiguous_assignment' } {
  if (templates.length === 0) {
    return { template: null, reason: 'no_template' };
  }
  if (!product) {
    return { template: null, reason: 'unresolved_product' };
  }

  let assignmentProduct = product;
  let matching = assignments.filter((assignment) => assignment.product === assignmentProduct);
  // Only an unassigned Non-QM scope may use the conventional template. An
  // invalid or ambiguous explicit assignment must remain visible to operators.
  if (matching.length === 0 && product === 'Non-QM') {
    assignmentProduct = 'Conforming';
    matching = assignments.filter((assignment) => assignment.product === assignmentProduct);
  }
  if (matching.length === 0) {
    return { template: null, reason: 'no_assignment' };
  }
  if (matching.length > 1) {
    return { template: null, reason: 'ambiguous_assignment' };
  }

  const template = templates.find((candidate) => candidate.id === matching[0].template_id) ?? null;
  return template ? { template, assignmentProduct, reason: 'ok' } : { template: null, reason: 'no_assignment' };
}
